import {
  TrendingUpOutlined,
  AttachMoneyOutlined,
  CheckCircleOutlined,
  AccessTimeOutlined,
  SendOutlined,
  ErrorOutline,
  ReceiptOutlined,
  InfoOutlined,
  CancelOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import {
  MetricCard,
  SearchBar,
  Dropdown,
  CardContainer,
  DataTable,
  Tag,
  Button,
  Tabs,
} from 'finara-components';
import type { DropdownOption, DataTableColumn, TabItem } from 'finara-components';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { ConfirmationModal, CepModal, LoadingState } from '@/components/common';
import { Navbar } from '@/components/super-admin/general/Navbar';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useOperationsMetrics,
  useScheduledTransactions,
  useCancelScheduledTransaction,
  useRetryTransaction,
} from '@/hooks/queries/useAnalytics';
import { useTransactions } from '@/hooks/queries/useTransactions';
import { useAuth } from '@/hooks/useAuth';
import { transactionsService } from '@/services/transactions.service';
import type { ScheduledTransaction } from '@/types/analytics';
import type { CepModalData } from '@/types/cep';
import type { Transaction, TransactionType, TransactionStatus } from '@/types/transactions';
import { formatNumber } from '@/utils/formatters';

// Helper function to check if transaction supports CEP
const isCepSupported = (tx: Transaction): boolean => {
  // CEP is only for STP (SPEI) transactions
  // - Type 'stp' (direct STP transactions)
  // - Type 'transfer' with STP indicators in meta

  // Check if type is explicitly 'stp'
  if (tx.type === 'stp') {
    return true;
  }

  // Check if it's a transfer with claveRastreo in meta (indicates STP processing)
  if (tx.type === 'transfer' && tx.meta && typeof tx.meta === 'object') {
    const meta = tx.meta;
    // Check for STP response or claveRastreo
    if ('stp_response' in meta || 'claveRastreo' in meta || 'stp_money_in_webhook' in meta) {
      return true;
    }
  }

  return false;
};

export const OperacionesGenerales: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<TransactionType | ''>('');
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'my' | 'all' | 'scheduled'>('my');

  // Modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);

  // CEP Modal state
  const [cepModalOpen, setCepModalOpen] = useState(false);
  const [cepData, setCepData] = useState<CepModalData | null>(null);
  const [loadingCep, setLoadingCep] = useState(false);

  // Mutations
  const cancelScheduledMutation = useCancelScheduledTransaction();
  const retryTransactionMutation = useRetryTransaction();

  // Fetch data from backend
  const { data: metrics, isLoading: metricsLoading } = useOperationsMetrics();

  const { data: myTransactions, isLoading: myTransactionsLoading } = useTransactions({
    user_id: user?.id || '',
    page: currentPage,
    limit: 10,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    startDate: dateFrom || undefined,
    endDate: dateTo || undefined,
  });

  // Super admin sees ALL platform transactions
  const { data: allTransactions, isLoading: allTransactionsLoading } = useTransactions({
    user_id: '', // Empty for all
    page: currentPage,
    limit: 10,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    startDate: dateFrom || undefined,
    endDate: dateTo || undefined,
  });

  const { data: scheduledTransactions, isLoading: scheduledLoading } = useScheduledTransactions({
    page: currentPage,
    limit: 10,
    status: statusFilter as
      | 'pending'
      | 'processing'
      | 'completed'
      | 'failed'
      | 'cancelled'
      | undefined,
  });

  // Filter options from API
  const typeOptions: DropdownOption[] = [
    { value: '', label: 'Todos los tipos' },
    { value: 'transfer', label: 'Transferencia' },
    { value: 'payroll', label: 'Nómina' },
    { value: 'credit', label: 'Crédito' },
    { value: 'debit', label: 'Débito' },
    { value: 'stp', label: 'SPEI' },
  ];

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'Todos los estatus' },
    { value: 'approved', label: 'Aprobado' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'processing', label: 'Procesando' },
    { value: 'rejected', label: 'Rechazado' },
    { value: 'failed', label: 'Fallido' },
  ];

  const handleResetFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const tabs: TabItem[] = [
    { id: 'my', title: 'Mis Transacciones', onSelect: () => setActiveTab('my') },
    {
      id: 'all',
      title: 'Todas las Transacciones (Plataforma)',
      onSelect: () => setActiveTab('all'),
    },
    {
      id: 'scheduled',
      title: 'Transacciones Programadas',
      onSelect: () => setActiveTab('scheduled'),
    },
  ];

  // Filter transactions by search term (client-side)
  const filteredMyTransactions = useMemo(() => {
    if (!myTransactions?.data) return [];
    if (!searchTerm) return myTransactions.data;

    return myTransactions.data.filter(
      tx =>
        (tx.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tx.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [myTransactions, searchTerm]);

  const filteredAllTransactions = useMemo(() => {
    if (!allTransactions?.data) return [];
    if (!searchTerm) return allTransactions.data;

    return allTransactions.data.filter(
      tx =>
        (tx.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tx.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allTransactions, searchTerm]);

  // Filter scheduled transactions by search term and date (client-side)
  const filteredScheduledTransactions = useMemo(() => {
    if (!scheduledTransactions?.data) return [];

    let filtered = scheduledTransactions.data;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        tx =>
          tx.beneficiaryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.concept.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.beneficiaryClabe.includes(searchTerm)
      );
    }

    // Filter by date range
    if (dateFrom || dateTo) {
      filtered = filtered.filter(tx => {
        const txDate = new Date(tx.scheduledDate);
        if (dateFrom && new Date(dateFrom) > txDate) return false;
        if (dateTo && new Date(dateTo) < txDate) return false;
        return true;
      });
    }

    return filtered;
  }, [scheduledTransactions, searchTerm, dateFrom, dateTo]);

  const getTypeLabel = (type: TransactionType): string => {
    const labels: Record<TransactionType, string> = {
      transfer: 'Transferencia',
      credit: 'Crédito',
      debit: 'Débito',
      payroll: 'Nómina',
      stp: 'SPEI',
    };
    return labels[type] || type;
  };

  const getTypeVariant = (type: TransactionType): 'blue' | 'red' | 'green' | 'gray' => {
    switch (type) {
      case 'payroll':
        return 'blue';
      case 'credit':
        return 'red';
      case 'debit':
        return 'green';
      case 'transfer':
      default:
        return 'gray';
    }
  };

  const getStatusVariant = (status: TransactionStatus) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'processing':
        return 'blue';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: TransactionStatus): string => {
    const labels: Record<TransactionStatus, string> = {
      approved: 'Aprobado',
      pending: 'Pendiente',
      processing: 'Procesando',
      rejected: 'Rechazado',
    };
    return labels[status];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleViewCep = async (tx: Transaction) => {
    if (!isCepSupported(tx)) {
      toast.error('Este tipo de transacción no soporta CEP');
      return;
    }

    setLoadingCep(true);
    try {
      const response = await transactionsService.getCep(tx.id);

      if (!response.success) {
        toast.error(
          response.message || 'La transacción no tiene clave de rastreo STP (claveRastreo)'
        );
        return;
      }

      // Set modal data
      setCepData({
        transactionId: response.transactionId,
        claveRastreo: response.claveRastreo,
        urlCEP: response.urlCEP,
        estado: response.estado,
        fechaOperacion: response.fechaOperacion,
        monto: response.monto,
        nombreBeneficiario: response.nombreBeneficiario,
        nombreOrdenante: response.nombreOrdenante,
        cuentaBeneficiario: response.cuentaBeneficiario,
        cuentaOrdenante: response.cuentaOrdenante,
        message: response.message,
      });
      setCepModalOpen(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al obtener el CEP';
      toast.error(errorMessage);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCancelScheduled = (id: string) => {
    setSelectedTransactionId(id);
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = () => {
    if (!selectedTransactionId) return;

    cancelScheduledMutation.mutate(selectedTransactionId, {
      onSuccess: () => {
        toast.success('Transacción programada cancelada exitosamente');
        setCancelModalOpen(false);
        setSelectedTransactionId(null);
      },
      onError: error => {
        toast.error(
          `Error al cancelar: ${error instanceof Error ? error.message : 'Error desconocido'}`
        );
        setCancelModalOpen(false);
        setSelectedTransactionId(null);
      },
    });
  };

  const handleRetryTransaction = (id: string) => {
    setSelectedTransactionId(id);
    setRetryModalOpen(true);
  };

  const handleConfirmRetry = () => {
    if (!selectedTransactionId) return;

    retryTransactionMutation.mutate(selectedTransactionId, {
      onSuccess: () => {
        toast.success('Transacción reintentada exitosamente');
        setRetryModalOpen(false);
        setSelectedTransactionId(null);
      },
      onError: error => {
        toast.error(
          `Error al reintentar: ${error instanceof Error ? error.message : 'Error desconocido'}`
        );
        setRetryModalOpen(false);
        setSelectedTransactionId(null);
      },
    });
  };

  // Table columns for transactions
  const transactionColumns: DataTableColumn<Transaction>[] = [
    {
      key: 'createdAt',
      label: 'Fecha/Hora',
      render: (_, tx) => (
        <div>
          <p className="text-sm text-brand-white">{formatDate(tx.createdAt)}</p>
        </div>
      ),
    },
    {
      key: 'reference',
      label: 'Referencia',
      render: (_, tx) => (
        <div>
          <p className="text-sm font-medium text-brand-white">{tx.reference}</p>
          <p className="text-xs text-muted">{tx.id.substring(0, 8)}</p>
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Descripción',
      render: (_, tx) => {
        const meta = tx.meta && typeof tx.meta === 'object' ? tx.meta : null;
        const beneficiary =
          meta && 'beneficiary' in meta && typeof meta['beneficiary'] === 'object'
            ? (meta['beneficiary'] as Record<string, unknown>)
            : null;
        const beneficiaryName =
          beneficiary && 'name' in beneficiary && typeof beneficiary['name'] === 'string'
            ? beneficiary['name']
            : '';

        return (
          <div>
            <p className="text-sm text-brand-white">{tx.description}</p>
            {beneficiaryName && <p className="text-xs text-muted">{beneficiaryName}</p>}
          </div>
        );
      },
    },
    {
      key: 'clabe',
      label: 'CLABE',
      render: (_, tx) => {
        const meta = tx.meta && typeof tx.meta === 'object' ? tx.meta : null;
        const beneficiary =
          meta && 'beneficiary' in meta && typeof meta['beneficiary'] === 'object'
            ? (meta['beneficiary'] as Record<string, unknown>)
            : null;
        const clabe =
          beneficiary && 'clabe' in beneficiary && typeof beneficiary['clabe'] === 'string'
            ? beneficiary['clabe']
            : '';
        return <span className="font-mono text-sm text-brand-white">{clabe || 'N/A'}</span>;
      },
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, tx) => <Tag variant={getTypeVariant(tx.type)}>{getTypeLabel(tx.type)}</Tag>,
    },
    {
      key: 'side',
      label: 'Dirección',
      render: (_, tx) => (
        <div>
          <p className="text-sm text-brand-white">{tx.side === 'in' ? 'Entrada' : 'Salida'}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, tx) => (
        <span className="text-sm font-semibold text-brand-white">
          ${formatNumber(tx.amount)} {tx.currency}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estatus',
      render: (_, tx) => (
        <Tag variant={getStatusVariant(tx.status)}>{getStatusLabel(tx.status)}</Tag>
      ),
    },
    {
      key: 'actions',
      label: 'CEP',
      render: (_, tx) =>
        isCepSupported(tx) ? (
          <button
            onClick={() => void handleViewCep(tx)}
            className="p-2 hover:bg-white/5 rounded-md transition-colors"
            title="Ver Comprobante Electrónico de Pago (CEP)"
            disabled={loadingCep}
          >
            <ReceiptOutlined fontSize="small" className="text-blue-600" />
          </button>
        ) : null,
    },
    {
      key: 'retry',
      label: 'Acciones',
      render: (_, tx) =>
        tx.status === 'rejected' ? (
          <button
            onClick={() => handleRetryTransaction(tx.id)}
            className="p-2 hover:bg-blue-500/10 rounded-md transition-colors"
            title="Reintentar transacción"
            disabled={retryTransactionMutation.isPending}
          >
            <RefreshOutlined fontSize="small" className="text-blue-600" />
          </button>
        ) : null,
    },
  ];

  // Table columns for scheduled transactions
  const scheduledColumns: DataTableColumn<ScheduledTransaction>[] = [
    {
      key: 'scheduledDate',
      label: 'Fecha Programada',
      render: (_, tx) => (
        <div>
          <p className="text-sm text-brand-white">{formatDate(tx.scheduledDate)}</p>
        </div>
      ),
    },
    {
      key: 'beneficiary',
      label: 'Beneficiario',
      render: (_, tx) => (
        <div>
          <p className="text-sm font-medium text-brand-white">{tx.beneficiaryName}</p>
          <p className="text-xs text-muted">{tx.beneficiaryClabe}</p>
        </div>
      ),
    },
    {
      key: 'concept',
      label: 'Concepto',
      render: (_, tx) => <span className="text-sm text-brand-white">{tx.concept}</span>,
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, tx) => (
        <span className="text-sm font-semibold text-brand-white">
          ${formatNumber(tx.amount)} {tx.currency}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estatus',
      render: (_, tx) => {
        const statusLabels = {
          pending: 'Pendiente',
          processing: 'Procesando',
          completed: 'Completado',
          failed: 'Fallido',
          cancelled: 'Cancelado',
        };
        const statusVariants: Record<string, 'yellow' | 'blue' | 'green' | 'red' | 'gray'> = {
          pending: 'yellow',
          processing: 'blue',
          completed: 'green',
          failed: 'red',
          cancelled: 'gray',
        };
        return <Tag variant={statusVariants[tx.status] || 'gray'}>{statusLabels[tx.status]}</Tag>;
      },
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, tx) =>
        tx.status === 'pending' ? (
          <button
            onClick={() => handleCancelScheduled(tx.id)}
            className="p-2 hover:bg-red-500/10 rounded-md transition-colors"
            title="Cancelar transacción programada"
            disabled={cancelScheduledMutation.isPending}
          >
            <CancelOutlined fontSize="small" className="text-red-600" />
          </button>
        ) : null,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'my':
        return (
          <div className="overflow-x-auto">
            {myTransactionsLoading ? (
              <div className="p-8 text-center text-muted">Cargando...</div>
            ) : filteredMyTransactions.length > 0 ? (
              <>
                <DataTable columns={transactionColumns} data={filteredMyTransactions} />
                {myTransactions && (
                  <div className="mt-4 flex justify-between items-center text-sm text-muted">
                    <span>
                      Mostrando {filteredMyTransactions.length} de {myTransactions.count}{' '}
                      transacciones
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="white"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Anterior
                      </Button>
                      <span className="px-3 py-1">
                        Página {currentPage} de{' '}
                        {Math.ceil(myTransactions.count / myTransactions.limit)}
                      </span>
                      <Button
                        variant="white"
                        size="small"
                        disabled={
                          currentPage >= Math.ceil(myTransactions.count / myTransactions.limit)
                        }
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center text-muted">No se encontraron transacciones</div>
            )}
          </div>
        );
      case 'all':
        return (
          <div className="overflow-x-auto">
            {allTransactionsLoading ? (
              <div className="p-8 text-center text-muted">Cargando...</div>
            ) : filteredAllTransactions.length > 0 ? (
              <>
                <DataTable columns={transactionColumns} data={filteredAllTransactions} />
                {allTransactions && (
                  <div className="mt-4 flex justify-between items-center text-sm text-muted">
                    <span>
                      Mostrando {filteredAllTransactions.length} de {allTransactions.count}{' '}
                      transacciones
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="white"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Anterior
                      </Button>
                      <span className="px-3 py-1">
                        Página {currentPage} de{' '}
                        {Math.ceil(allTransactions.count / allTransactions.limit)}
                      </span>
                      <Button
                        variant="white"
                        size="small"
                        disabled={
                          currentPage >= Math.ceil(allTransactions.count / allTransactions.limit)
                        }
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center text-muted">No se encontraron transacciones</div>
            )}
          </div>
        );
      case 'scheduled':
        return (
          <div className="overflow-x-auto">
            {scheduledLoading ? (
              <div className="p-8 text-center text-muted">Cargando...</div>
            ) : filteredScheduledTransactions.length > 0 ? (
              <>
                <DataTable columns={scheduledColumns} data={filteredScheduledTransactions} />
                {scheduledTransactions && scheduledTransactions.count > 0 && (
                  <div className="mt-4 flex justify-between items-center text-sm text-muted">
                    <span>
                      Mostrando {filteredScheduledTransactions.length} de{' '}
                      {scheduledTransactions.count} programadas
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="white"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Anterior
                      </Button>
                      <span className="px-3 py-1">
                        Página {currentPage} de{' '}
                        {Math.ceil(scheduledTransactions.count / scheduledTransactions.limit)}
                      </span>
                      <Button
                        variant="white"
                        size="small"
                        disabled={
                          currentPage >=
                          Math.ceil(scheduledTransactions.count / scheduledTransactions.limit)
                        }
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center text-muted">No hay transacciones programadas</div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} userName={user?.name || 'Usuario'} />

      <div className="px-4 py-8">
        {/* Metrics Cards */}
        {metricsLoading ? (
          <div className="grid grid-cols-1 md:!grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <CardContainer key={i} className="p-6 h-32 animate-pulse bg-white/5" />
            ))}
          </div>
        ) : metrics ? (
          <div className="grid grid-cols-1 md:!grid-cols-4 gap-6 mb-6">
            <MetricCard
              title="Operaciones Hoy (Plataforma)"
              value={metrics.operationsToday.toString()}
              description={`${metrics.operationsThisWeek} esta semana (${metrics.operationsWeekChange >= 0 ? '+' : ''}${metrics.operationsWeekChange.toFixed(1)}%)`}
              icon={<TrendingUpOutlined className="text-green-600" />}
              variant={metrics.operationsWeekChange >= 0 ? 'green' : 'red'}
            />
            <MetricCard
              title="Volumen Procesado Hoy"
              value={`$${formatNumber(metrics.volumeToday)}`}
              description={`$${formatNumber(metrics.volumeThisMonth)} este mes (${metrics.volumeMonthChange >= 0 ? '+' : ''}${metrics.volumeMonthChange.toFixed(1)}%)`}
              icon={<AttachMoneyOutlined className="text-green-600" />}
              variant={metrics.volumeMonthChange >= 0 ? 'green' : 'red'}
            />
            <MetricCard
              title="Tasa de Éxito"
              value={`${metrics.successRate.toFixed(1)}%`}
              description={`${metrics.failedOperations} fallidas hoy (${metrics.successRateChange >= 0 ? '+' : ''}${metrics.successRateChange.toFixed(1)}%)`}
              icon={<CheckCircleOutlined className="text-green-600" />}
              variant={metrics.successRate >= 95 ? 'green' : 'yellow'}
            />
            <MetricCard
              title="Montos Pendientes"
              value={`$${formatNumber(metrics.pendingAmount)}`}
              description={`${metrics.pendingCount} programados, ${metrics.pendingValidation} requieren validación`}
              icon={
                metrics.pendingValidation > 0 ? (
                  <ErrorOutline className="text-orange-600" />
                ) : (
                  <AccessTimeOutlined className="text-blue-600" />
                )
              }
              variant={metrics.pendingValidation > 0 ? 'yellow' : 'white'}
            />
          </div>
        ) : null}

        {/* Filters Section */}
        <CardContainer className="mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-white mb-4">Filtros</h3>

            {/* Search Bar */}
            <div className="mb-4">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar por referencia o descripción"
              />
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:!grid-cols-4 gap-4 mb-4">
              <div>
                <label
                  htmlFor="type-filter"
                  className="block text-sm font-medium text-brand-white mb-2"
                >
                  Tipo
                </label>
                <Dropdown
                  value={typeFilter}
                  onChange={value => setTypeFilter(String(value) as TransactionType | '')}
                  options={typeOptions}
                  placeholder="Todos los tipos"
                />
              </div>
              <div>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-brand-white mb-2"
                >
                  Estatus
                </label>
                <Dropdown
                  value={statusFilter}
                  onChange={value => setStatusFilter(String(value) as TransactionStatus | '')}
                  options={statusOptions}
                  placeholder="Todos los estatus"
                />
              </div>
              <div>
                <label
                  htmlFor="date-from"
                  className="block text-sm font-medium text-brand-white mb-2"
                >
                  Desde
                </label>
                <input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-surface text-brand-white rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue"
                />
              </div>
              <div>
                <label
                  htmlFor="date-to"
                  className="block text-sm font-medium text-brand-white mb-2"
                >
                  Hasta
                </label>
                <input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-surface text-brand-white rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue"
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button variant="white" size="small" onClick={handleResetFilters}>
                Limpiar Filtros
              </Button>
              <Button
                variant="black"
                size="small"
                startIcon={<SendOutlined />}
                onClick={() => void navigate('/pagos')}
              >
                Nuevo Pago
              </Button>
            </div>
          </div>
        </CardContainer>

        {/* CEP Disclaimer */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <InfoOutlined className="text-blue-600 mt-0.5" fontSize="small" />
            <div className="text-sm text-brand-white">
              <span className="font-semibold">Nota sobre CEP:</span> El Comprobante Electrónico de
              Pago (CEP) está disponible únicamente para transacciones SPEI procesadas a través de
              STP (transferencias externas salientes y entrantes). No aplica para transferencias
              internas, pagos de servicios, créditos, ahorros o nómina.
            </div>
          </div>
        </div>

        {/* Transactions Tables with Tabs */}
        <CardContainer>
          <Tabs tabs={tabs} activeTabId={activeTab} />
          <div className="mt-4">{renderTabContent()}</div>
        </CardContainer>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancelar Transacción Programada"
        message="¿Estás seguro de que deseas cancelar esta transacción programada? Esta acción no se puede deshacer."
        confirmText="Sí, cancelar"
        cancelText="No, volver"
        isLoading={cancelScheduledMutation.isPending}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={retryModalOpen}
        onClose={() => setRetryModalOpen(false)}
        onConfirm={handleConfirmRetry}
        title="Reintentar Transacción"
        message="¿Deseas reintentar esta transacción fallida? Se procesará nuevamente."
        confirmText="Sí, reintentar"
        cancelText="Cancelar"
        isLoading={retryTransactionMutation.isPending}
        variant="info"
      />

      {/* CEP Modal */}
      <CepModal isOpen={cepModalOpen} onClose={() => setCepModalOpen(false)} data={cepData} />

      {/* Loading State for CEP */}
      {loadingCep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-surface rounded-lg p-8 border border-border">
            <LoadingState message="Obteniendo información del CEP..." />
          </div>
        </div>
      )}
    </div>
  );
};
